<!-- src/components/tasks/KanbanBoard.vue -->

<template>
  <div class="core-page core-page--full kanban-board">
    <!-- =====================================================
         HEADER
    ====================================================== -->
    <div class="core-page-header kanban-page-header">
      <div class="core-page-header-main">
        <div v-if="editingBoardTitle" class="kanban-board-title-editor">
          <input
            ref="boardTitleInput"
            v-model.trim="localBoardTitle"
            class="core-input"
            type="text"
            maxlength="100"
            @blur="finishBoardTitleEdit"
            @keyup.enter="finishBoardTitleEdit"
            @keyup.esc="cancelBoardTitleEdit"
          />
        </div>

        <template v-else>
          <button
            type="button"
            class="btn-flat kanban-board-title-button"
            :disabled="readonly"
            @click="startBoardTitleEdit"
          >
            <h4 class="core-title">
              {{ localBoardTitle }}
            </h4>
          </button>

          <p class="core-subtitle">Manage tasks and workflow</p>
        </template>
      </div>

      <div
        v-if="!readonly"
        class="core-page-header-actions core-actions core-actions--right"
      >
        <button type="button" class="btn blue-grey darken-2" @click="openAddColumn">
          <i class="material-icons">add</i>
          Add List
        </button>
      </div>
    </div>

    <!-- =====================================================
         TOOLBAR
    ====================================================== -->
    <div class="core-toolbar-panel">
      <div class="core-toolbar">
        <div class="core-toolbar-main kanban-search">
          <div class="core-field">
            <label class="core-field-label">Search cards</label>

            <div class="core-field-row">
              <input
                v-model.trim="searchTerm"
                class="core-input"
                type="search"
                placeholder="Search title, description, assignee or label..."
              />

              <button
                v-if="searchTerm"
                type="button"
                class="core-icon-button core-field-action"
                title="Clear"
                @click="searchTerm = ''"
              >
                <i class="material-icons">close</i>
              </button>
            </div>
          </div>
        </div>

        <div class="core-toolbar-actions">
          <span class="core-badge core-badge--soft-rect">
            {{ totalVisibleCards }}
            {{ totalVisibleCards === 1 ? "card" : "cards" }}
          </span>
        </div>
      </div>
    </div>

    <!-- =====================================================
         WORKFLOW PROGRESS
    ====================================================== -->
    <div class="kanban-workflow-progress">
      <div class="kanban-workflow-progress-header">
        <div>
          <div class="core-small-title">Workflow progress</div>

          <div class="core-card-subtitle">
            {{ completedCardsCount }} completed · {{ inProgressCardsCount }} in progress
          </div>
        </div>

        <span
          class="core-badge core-badge--soft-rect kanban-workflow-health"
          :class="workflowHealthClass"
        >
          {{ completedPercent }}% completed
        </span>
      </div>

      <div
        class="kanban-workflow-track"
        role="progressbar"
        aria-label="Workflow completion"
        aria-valuemin="0"
        aria-valuemax="100"
        :aria-valuenow="completedPercent"
      >
        <div
          v-if="workflowTrackedTotal > 0"
          class="kanban-workflow-segment kanban-workflow-segment--completed"
          :style="{ width: `${completedPercent}%` }"
        ></div>

        <div
          v-if="workflowTrackedTotal > 0"
          class="kanban-workflow-segment kanban-workflow-segment--in-progress"
          :style="{ width: `${inProgressPercent}%` }"
        ></div>
      </div>

      <div class="kanban-workflow-legend">
        <span>
          <span class="kanban-workflow-dot kanban-workflow-dot--completed"></span>
          Completed
        </span>

        <span>
          <span class="kanban-workflow-dot kanban-workflow-dot--in-progress"></span>
          In Progress
        </span>

        <span v-if="workflowTrackedTotal > 0">
          {{ inProgressPercent }}% currently in progress
        </span>

        <span v-else> No started tasks yet </span>
      </div>
    </div>

    <!-- =====================================================
         BOARD
    ====================================================== -->
    <div ref="boardScroller" class="kanban-board-scroll">
      <div class="kanban-columns">
        <section
          v-for="column in columns"
          :key="column.id"
          class="core-panel core-panel--soft kanban-column"
          :class="{
            'kanban-column--drag-target':
              dragState.cardId && dragState.overColumnId === column.id,
          }"
          @dragover.prevent="onColumnDragOver(column.id)"
          @drop.prevent="onDropAtEnd(column.id)"
        >
          <!-- =================================================
               COLUMN HEADER
          ================================================== -->
          <div class="kanban-column-header">
            <div class="kanban-column-heading">
              <input
                v-if="editingColumnId === column.id"
                v-model.trim="column.title"
                type="text"
                maxlength="80"
                class="core-input kanban-column-title-input"
                @blur="finishColumnTitleEdit"
                @keyup.enter="finishColumnTitleEdit"
                @keyup.esc="finishColumnTitleEdit"
              />

              <template v-else>
                <button
                  type="button"
                  class="btn-flat kanban-column-title-button"
                  :disabled="readonly"
                  @click="startColumnTitleEdit(column)"
                >
                  <span class="core-small-title">
                    {{ column.title }}
                  </span>
                </button>

                <span class="core-badge core-badge--small">
                  {{ visibleCards(column).length }}
                </span>
              </template>
            </div>

            <div v-if="!readonly" class="kanban-column-menu-wrap">
              <button
                type="button"
                class="core-icon-button"
                title="List options"
                @click.stop="toggleColumnMenu(column.id)"
              >
                <i class="material-icons">more_horiz</i>
              </button>

              <div
                v-if="openColumnMenuId === column.id"
                class="core-panel core-card--shadow kanban-column-menu"
                @click.stop
              >
                <button
                  type="button"
                  class="btn-flat"
                  @click="startColumnTitleEdit(column)"
                >
                  <i class="material-icons">edit</i>
                  Rename List
                </button>

                <button type="button" class="btn-flat" @click="openAddCard(column.id)">
                  <i class="material-icons">add</i>
                  Add Card
                </button>

                <button
                  type="button"
                  class="btn-flat"
                  @click="duplicateColumn(column.id)"
                >
                  <i class="material-icons">content_copy</i>
                  Duplicate List
                </button>

                <div class="core-divider"></div>

                <button
                  type="button"
                  class="btn-flat red-text text-darken-2"
                  @click="deleteColumn(column.id)"
                >
                  <i class="material-icons">delete</i>
                  Delete List
                </button>
              </div>
            </div>
          </div>

          <!-- =================================================
               CARDS
          ================================================== -->
          <div class="kanban-card-list">
            <template v-for="card in visibleCards(column)" :key="card.id">
              <div
                v-if="dragState.cardId && !searchTerm"
                class="kanban-drop-slot"
                :class="{
                  'kanban-drop-slot--active':
                    dragState.overColumnId === column.id &&
                    dragState.overIndex === actualCardIndex(column, card),
                }"
                @dragenter.prevent="
                  setDropPosition(column.id, actualCardIndex(column, card))
                "
                @dragover.prevent
                @drop.prevent.stop="onDrop(column.id, actualCardIndex(column, card))"
              ></div>

              <article
                class="core-card core-card--bordered core-card--shadow kanban-card"
                :class="{
                  'kanban-card--dragging': dragState.cardId === card.id,
                }"
                :draggable="!readonly && !searchTerm"
                @dragstart="onDragStart($event, column.id, card.id)"
                @dragend="onDragEnd"
                @click="openEditCard(column.id, card.id)"
              >
                <div class="core-card-body">
                  <!-- LABELS -->
                  <div v-if="card.labels?.length" class="core-inline kanban-labels">
                    <span
                      v-for="label in card.labels"
                      :key="label.id"
                      class="core-badge core-badge--small core-badge--soft-rect kanban-label"
                      :style="labelStyle(label)"
                    >
                      {{ label.name }}
                    </span>
                  </div>

                  <!-- SOURCE -->
                  <div v-if="card.source?.type === 'gmail'" class="kanban-source">
                    <span class="core-badge core-badge--small core-badge--neutral">
                      <i class="material-icons kanban-meta-icon"> email </i>
                      Gmail
                    </span>
                  </div>

                  <!-- TITLE -->
                  <div class="kanban-card-title">
                    {{ card.title }}
                  </div>

                  <!-- DESCRIPTION -->
                  <p
                    v-if="card.description"
                    class="core-card-subtitle kanban-card-description"
                  >
                    {{ card.description }}
                  </p>

                  <!-- META -->
                  <div
                    v-if="
                      card.dueDate ||
                      card.assignee ||
                      card.description ||
                      checklistTotal(card) > 0
                    "
                    class="core-inline kanban-card-meta"
                  >
                    <span
                      v-if="card.dueDate"
                      class="core-badge core-badge--small"
                      :class="dueDateBadgeClass(card)"
                    >
                      <i class="material-icons kanban-meta-icon"> schedule </i>

                      {{ formatDate(card.dueDate) }}
                    </span>

                    <span v-if="card.description" class="core-badge core-badge--small">
                      <i class="material-icons kanban-meta-icon"> notes </i>
                    </span>

                    <span
                      v-if="checklistTotal(card)"
                      class="core-badge core-badge--small"
                      :class="{
                        'core-badge--success':
                          checklistComplete(card) === checklistTotal(card),
                      }"
                    >
                      <i class="material-icons kanban-meta-icon"> checklist </i>

                      {{ checklistComplete(card) }}/{{ checklistTotal(card) }}
                    </span>

                    <span
                      v-if="card.assignee"
                      class="kanban-assignee"
                      :title="card.assignee"
                    >
                      {{ initials(card.assignee) }}
                    </span>
                  </div>
                </div>
              </article>
            </template>

            <!-- EMPTY -->
            <div
              v-if="visibleCards(column).length === 0"
              class="core-empty-state core-empty-state--inline kanban-empty"
            >
              <i class="material-icons">inbox</i>

              <span>
                {{ searchTerm ? "No matching cards" : "No cards" }}
              </span>
            </div>

            <!-- END DROP TARGET -->
            <div
              v-if="dragState.cardId && !searchTerm"
              class="kanban-drop-slot"
              :class="{
                'kanban-drop-slot--active':
                  dragState.overColumnId === column.id &&
                  dragState.overIndex === column.cards.length,
              }"
              @dragenter.prevent="setDropPosition(column.id, column.cards.length)"
              @dragover.prevent
              @drop.prevent.stop="onDrop(column.id, column.cards.length)"
            ></div>
          </div>

          <!-- ADD CARD -->
          <button
            v-if="!readonly"
            type="button"
            class="btn-flat kanban-add-card-button"
            @click="openAddCard(column.id)"
          >
            <i class="material-icons">add</i>
            Add Card
          </button>
        </section>

        <!-- ADD LIST -->
        <button
          v-if="!readonly"
          type="button"
          class="btn blue-grey lighten-1 kanban-add-column"
          @click="openAddColumn"
        >
          <i class="material-icons">add</i>
          Add Another List
        </button>
      </div>
    </div>

    <!-- =====================================================
         MODALS
    ====================================================== -->
    <Teleport to="body">
      <!-- CARD -->
      <div
        v-if="cardModalOpen"
        class="core-modal-backdrop"
        @mousedown.self="closeCardModal"
      >
        <section class="core-modal core-modal--medium" role="dialog" aria-modal="true">
          <div class="core-modal-header">
            <div>
              <h5 class="core-card-title">
                {{ cardEditor.id ? "Edit Card" : "Add Card" }}
              </h5>

              <p class="core-card-subtitle">
                In
                <strong>
                  {{ getColumn(cardEditorColumnId)?.title || "" }}
                </strong>
              </p>
            </div>

            <button type="button" class="core-icon-button" @click="closeCardModal">
              <i class="material-icons">close</i>
            </button>
          </div>

          <div class="core-modal-body">
            <form class="core-form" @submit.prevent="saveCard">
              <!-- TITLE -->
              <div class="core-field">
                <label class="core-field-label"> Card Title </label>

                <input
                  v-model="cardEditor.title"
                  type="text"
                  class="core-input"
                  maxlength="150"
                />
              </div>

              <!-- DESCRIPTION -->
              <div class="core-field kanban-form-section">
                <label class="core-field-label"> Description </label>

                <textarea
                  v-model="cardEditor.description"
                  class="core-textarea core-textarea--large"
                  maxlength="5000"
                ></textarea>
              </div>

              <!-- EMAIL SOURCE -->
              <div
                v-if="cardEditor.source?.type === 'gmail'"
                class="core-panel core-panel--soft kanban-form-section"
              >
                <div class="core-small-title">Source Email</div>

                <div v-if="cardEditor.source.sender" class="core-card-subtitle">
                  From:
                  {{ cardEditor.source.sender }}
                </div>

                <div v-if="cardEditor.source.subject" class="core-card-subtitle">
                  Subject:
                  {{ cardEditor.source.subject }}
                </div>

                <div v-if="cardEditor.source.emailDate" class="core-card-subtitle">
                  Received:
                  {{ cardEditor.source.emailDate }}
                </div>
              </div>

              <!-- ASSIGNEE / DUE -->
              <div class="core-form-grid core-form-grid--2 kanban-form-section">
                <div class="core-field">
                  <label class="core-field-label"> Assignee </label>

                  <input
                    v-model="cardEditor.assignee"
                    type="text"
                    class="core-input"
                    maxlength="100"
                  />
                </div>

                <div class="core-field">
                  <label class="core-field-label"> Due Date </label>

                  <input v-model="cardEditor.dueDate" type="date" class="core-input" />
                </div>
              </div>

              <!-- LIST -->
              <div class="core-field kanban-form-section">
                <label class="core-field-label"> List </label>

                <select v-model="cardEditorColumnId" class="core-native-select">
                  <option v-for="column in columns" :key="column.id" :value="column.id">
                    {{ column.title }}
                  </option>
                </select>
              </div>

              <!-- LABELS -->
              <div class="core-form-section">
                <h6 class="core-form-section-title">Labels</h6>

                <div class="core-stack core-stack--small">
                  <div
                    v-for="label in cardEditor.labels"
                    :key="label.id"
                    class="kanban-label-edit-row"
                  >
                    <input
                      v-model="label.color"
                      type="color"
                      class="kanban-colour-input"
                    />

                    <input
                      v-model="label.name"
                      type="text"
                      class="core-input"
                      maxlength="50"
                    />

                    <button
                      type="button"
                      class="core-icon-button red-text"
                      @click="removeEditorLabel(label.id)"
                    >
                      <i class="material-icons"> close </i>
                    </button>
                  </div>
                </div>

                <button type="button" class="btn-flat" @click="addEditorLabel">
                  <i class="material-icons">add</i>
                  Add Label
                </button>
              </div>

              <!-- CHECKLIST -->
              <div class="core-form-section">
                <div class="kanban-checklist-heading">
                  <h6 class="core-form-section-title">Checklist</h6>

                  <span v-if="cardEditor.checklist.length" class="core-badge">
                    {{ editorChecklistComplete }}/{{ cardEditor.checklist.length }}
                  </span>
                </div>

                <div v-if="cardEditor.checklist.length" class="progress">
                  <div
                    class="determinate"
                    :style="{
                      width: `${editorChecklistPercent}%`,
                    }"
                  ></div>
                </div>

                <div class="core-stack core-stack--small">
                  <div
                    v-for="item in cardEditor.checklist"
                    :key="item.id"
                    class="kanban-checklist-row"
                  >
                    <label>
                      <input v-model="item.completed" type="checkbox" />
                      <span></span>
                    </label>

                    <input
                      v-model="item.text"
                      type="text"
                      class="core-input"
                      maxlength="200"
                    />

                    <button
                      type="button"
                      class="core-icon-button red-text"
                      @click="removeChecklistItem(item.id)"
                    >
                      <i class="material-icons"> close </i>
                    </button>
                  </div>
                </div>

                <button type="button" class="btn-flat" @click="addChecklistItem">
                  <i class="material-icons">add</i>
                  Add Checklist Item
                </button>
              </div>

              <!-- COMPLETE -->
              <div v-if="cardEditor.id" class="core-form-section">
                <label>
                  <input v-model="cardEditor.completed" type="checkbox" />

                  <span> Mark card as complete </span>
                </label>

                <p class="core-card-subtitle kanban-complete-hint">
                  Completed cards are automatically moved to the Completed list when
                  saved.
                </p>
              </div>
            </form>
          </div>

          <div class="core-modal-actions">
            <button
              v-if="cardEditor.id && !readonly"
              type="button"
              class="btn-flat red-text"
              @click="deleteCurrentCard"
            >
              <i class="material-icons"> delete </i>

              Delete Card
            </button>

            <div class="core-spacer"></div>

            <button type="button" class="btn-flat" @click="closeCardModal">Cancel</button>

            <button
              v-if="!readonly"
              type="button"
              class="btn teal darken-2"
              :disabled="!cardEditor.title.trim()"
              @click="saveCard"
            >
              <i class="material-icons">save</i>

              {{ cardEditor.id ? "Save Changes" : "Add Card" }}
            </button>
          </div>
        </section>
      </div>

      <!-- COLUMN -->
      <div
        v-if="columnModalOpen"
        class="core-modal-backdrop"
        @mousedown.self="closeColumnModal"
      >
        <section class="core-modal core-modal--small">
          <div class="core-modal-header">
            <h5 class="core-card-title">Add List</h5>

            <button type="button" class="core-icon-button" @click="closeColumnModal">
              <i class="material-icons"> close </i>
            </button>
          </div>

          <div class="core-modal-body">
            <div class="core-field">
              <label class="core-field-label"> List Name </label>

              <input
                v-model="newColumnTitle"
                type="text"
                class="core-input"
                maxlength="80"
                @keyup.enter="addColumn"
              />
            </div>
          </div>

          <div class="core-modal-actions">
            <div class="core-spacer"></div>

            <button type="button" class="btn-flat" @click="closeColumnModal">
              Cancel
            </button>

            <button
              type="button"
              class="btn teal darken-2"
              :disabled="!newColumnTitle.trim()"
              @click="addColumn"
            >
              Add List
            </button>
          </div>
        </section>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  watch,
} from "vue";

/* =========================================================
   PROPS
========================================================= */

const props = defineProps({
  board: {
    type: Object,
    required: true,
  },

  readonly: {
    type: Boolean,
    default: false,
  },
});

/* =========================================================
   EMITS
========================================================= */

const emit = defineEmits([
  "change",
  "card-created",
  "card-updated",
  "card-deleted",
  "card-moved",
  "column-created",
  "column-deleted",
]);

/* =========================================================
   HELPERS
========================================================= */

function createId(prefix) {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return `${prefix}-${crypto.randomUUID()}`;
  }

  return `${prefix}-${Date.now()}-` + Math.random().toString(36).slice(2, 10);
}

function clone(value) {
  if (typeof structuredClone === "function") {
    return structuredClone(value);
  }

  return JSON.parse(JSON.stringify(value));
}

/* =========================================================
   STATE
========================================================= */

const localBoardTitle = ref("Tasks");

const columns = ref([]);

const revision = ref(0);

const searchTerm = ref("");

const boardScroller = ref(null);

/* =========================================================
   LOAD PROP BOARD
========================================================= */

function applyBoard(value) {
  localBoardTitle.value = value?.title || "Tasks";

  revision.value = Number(value?.revision || 0);

  const nextColumns = Array.isArray(value?.columns) ? clone(value.columns) : [];

  nextColumns.forEach((column) => {
    column.cards = Array.isArray(column.cards) ? column.cards : [];

    const role = columnRole(column);

    /*
     * These three list names are the workflow.
     *
     * Existing "Done" boards are migrated locally to
     * "Completed" without losing any cards.
     */
    if (role === "todo") {
      column.title = "To Do";

      column.cards.forEach((card) => {
        card.completed = false;
      });
    }

    if (role === "inProgress") {
      column.title = "In Progress";

      column.cards.forEach((card) => {
        card.completed = false;
      });
    }

    if (role === "completed") {
      column.title = "Completed";

      column.cards.forEach((card) => {
        card.completed = true;
      });
    }
  });

  columns.value = nextColumns;
}

watch(
  () => props.board,

  (value) => {
    applyBoard(value);
  },

  {
    immediate: true,
    deep: true,
  }
);

/* =========================================================
   EMIT BOARD
========================================================= */

function emitBoardChange() {
  emit("change", {
    ...clone(props.board),

    title: localBoardTitle.value,

    revision: revision.value,

    columns: clone(columns.value),
  });
}

/* =========================================================
   SEARCH
========================================================= */

function visibleCards(column) {
  if (!searchTerm.value) {
    return column.cards;
  }

  const term = searchTerm.value.toLowerCase();

  return column.cards.filter((card) => {
    const text = [
      card.title,
      card.description,
      card.assignee,

      ...(card.labels || []).map((label) => label.name),
    ]
      .join(" ")
      .toLowerCase();

    return text.includes(term);
  });
}

const totalVisibleCards = computed(() => {
  return columns.value.reduce(
    (total, column) => total + visibleCards(column).length,

    0
  );
});

/* =========================================================
   WORKFLOW PROGRESS
========================================================= */

const inProgressCardsCount = computed(() => {
  return getWorkflowColumn("inProgress")?.cards.length || 0;
});

const completedCardsCount = computed(() => {
  return getWorkflowColumn("completed")?.cards.length || 0;
});

const workflowTrackedTotal = computed(() => {
  return inProgressCardsCount.value + completedCardsCount.value;
});

const inProgressPercent = computed(() => {
  if (!workflowTrackedTotal.value) {
    return 0;
  }

  return Math.round((inProgressCardsCount.value / workflowTrackedTotal.value) * 100);
});

const completedPercent = computed(() => {
  if (!workflowTrackedTotal.value) {
    return 0;
  }

  return 100 - inProgressPercent.value;
});

const workflowHealthClass = computed(() => {
  if (!workflowTrackedTotal.value) {
    return "kanban-workflow-health--neutral";
  }

  if (completedCardsCount.value > inProgressCardsCount.value) {
    return "kanban-workflow-health--green";
  }

  if (inProgressCardsCount.value > completedCardsCount.value) {
    return "kanban-workflow-health--red";
  }

  return "kanban-workflow-health--amber";
});

function actualCardIndex(column, card) {
  return column.cards.findIndex((item) => item.id === card.id);
}

/* =========================================================
   BOARD TITLE
========================================================= */

const editingBoardTitle = ref(false);

const boardTitleInput = ref(null);

let previousBoardTitle = "";

async function startBoardTitleEdit() {
  if (props.readonly) {
    return;
  }

  previousBoardTitle = localBoardTitle.value;

  editingBoardTitle.value = true;

  await nextTick();

  boardTitleInput.value?.focus();

  boardTitleInput.value?.select();
}

function finishBoardTitleEdit() {
  if (!localBoardTitle.value.trim()) {
    localBoardTitle.value = previousBoardTitle || "Tasks";
  }

  editingBoardTitle.value = false;

  emitBoardChange();
}

function cancelBoardTitleEdit() {
  localBoardTitle.value = previousBoardTitle;

  editingBoardTitle.value = false;
}

/* =========================================================
   COLUMN MENU
========================================================= */

const openColumnMenuId = ref(null);

function toggleColumnMenu(columnId) {
  openColumnMenuId.value = openColumnMenuId.value === columnId ? null : columnId;
}

function closeMenus() {
  openColumnMenuId.value = null;
}

/* =========================================================
   ADD COLUMN
========================================================= */

const columnModalOpen = ref(false);

const newColumnTitle = ref("");

function openAddColumn() {
  closeMenus();

  newColumnTitle.value = "";

  columnModalOpen.value = true;
}

function closeColumnModal() {
  columnModalOpen.value = false;

  newColumnTitle.value = "";
}

function addColumn() {
  const title = newColumnTitle.value.trim();

  if (!title) {
    return;
  }

  const column = {
    id: createId("column"),
    title,
    cards: [],
  };

  columns.value.push(column);

  emit("column-created", clone(column));

  emitBoardChange();

  closeColumnModal();

  nextTick(() => {
    if (boardScroller.value) {
      boardScroller.value.scrollLeft = boardScroller.value.scrollWidth;
    }
  });
}

/* =========================================================
   COLUMN TITLE
========================================================= */

const editingColumnId = ref(null);

async function startColumnTitleEdit(column) {
  closeMenus();

  editingColumnId.value = column.id;

  await nextTick();

  const input = document.querySelector(".kanban-column-title-input");

  input?.focus();

  input?.select();
}

function finishColumnTitleEdit() {
  const column = getColumn(editingColumnId.value);

  if (column && !column.title.trim()) {
    column.title = "Untitled";
  }

  /*
   * Keep the three workflow lists
   * consistently named.
   */
  if (column) {
    const role = columnRole(column);

    if (role === "todo") {
      column.title = "To Do";
    }

    if (role === "inProgress") {
      column.title = "In Progress";
    }

    if (role === "completed") {
      column.title = "Completed";
    }
  }

  editingColumnId.value = null;

  emitBoardChange();
}

/* =========================================================
   DUPLICATE COLUMN
========================================================= */

function duplicateColumn(columnId) {
  const source = getColumn(columnId);

  if (!source) {
    return;
  }

  const copy = {
    id: createId("column"),

    title: `${source.title} Copy`,

    cards: source.cards.map((card) => ({
      ...clone(card),

      id: createId("card"),

      labels: (card.labels || []).map((label) => ({
        ...clone(label),

        id: createId("label"),
      })),

      checklist: (card.checklist || []).map((item) => ({
        ...clone(item),

        id: createId("check"),
      })),

      source: null,

      createdAt: new Date().toISOString(),

      updatedAt: new Date().toISOString(),
    })),
  };

  const index = columns.value.findIndex((column) => column.id === columnId);

  columns.value.splice(index + 1, 0, copy);

  emitBoardChange();

  closeMenus();
}

/* =========================================================
   DELETE COLUMN
========================================================= */

function deleteColumn(columnId) {
  const column = getColumn(columnId);

  if (!column) {
    return;
  }

  const count = column.cards.length;

  let message = `Delete "${column.title}"?`;

  if (count) {
    message +=
      `\n\nThis will also delete ${count} ` + `${count === 1 ? "card" : "cards"}.`;
  }

  if (!window.confirm(message)) {
    return;
  }

  columns.value = columns.value.filter((item) => item.id !== columnId);

  emit("column-deleted", {
    columnId,
  });

  emitBoardChange();

  closeMenus();
}

/* =========================================================
   CARD EDITOR
========================================================= */

const cardModalOpen = ref(false);

const cardEditorColumnId = ref(null);

const originalCardColumnId = ref(null);

/*
 * When a To Do card is opened we remember it here.
 *
 * The actual move occurs when the editor closes, as
 * requested, rather than immediately when it opens.
 */
const autoAdvanceOpenedCardId = ref(null);

const autoAdvanceSourceColumnId = ref(null);

const cardEditor = reactive({
  id: null,

  title: "",

  description: "",

  assignee: "",

  dueDate: "",

  completed: false,

  labels: [],

  checklist: [],

  source: null,

  createdAt: null,

  createdBy: "",
});

function resetCardEditor() {
  cardEditor.id = null;

  cardEditor.title = "";

  cardEditor.description = "";

  cardEditor.assignee = "";

  cardEditor.dueDate = "";

  cardEditor.completed = false;

  cardEditor.labels = [];

  cardEditor.checklist = [];

  cardEditor.source = null;

  cardEditor.createdAt = null;

  cardEditor.createdBy = "";

  cardEditorColumnId.value = null;

  originalCardColumnId.value = null;

  autoAdvanceOpenedCardId.value = null;

  autoAdvanceSourceColumnId.value = null;
}

/*
 * ALL manually-created cards go into To Do.
 *
 * It does not matter which Add Card button the user
 * clicked. If a To Do column exists it is always used.
 */
function openAddCard(columnId) {
  resetCardEditor();

  closeMenus();

  const todoColumn = getWorkflowColumn("todo");

  const targetColumnId = todoColumn?.id || columnId;

  cardEditorColumnId.value = targetColumnId;

  originalCardColumnId.value = targetColumnId;

  cardModalOpen.value = true;
}

function openEditCard(columnId, cardId) {
  const column = getColumn(columnId);

  const card = column?.cards.find((item) => item.id === cardId);

  if (!card) {
    return;
  }

  resetCardEditor();

  Object.assign(cardEditor, clone(card));

  cardEditor.labels = clone(card.labels || []);

  cardEditor.checklist = clone(card.checklist || []);

  cardEditor.source = card.source ? clone(card.source) : null;

  cardEditorColumnId.value = columnId;

  originalCardColumnId.value = columnId;

  /*
   * Only editable boards should automatically advance
   * cards. Opening a read-only card must not mutate it.
   */
  if (!props.readonly && columnRole(column) === "todo") {
    autoAdvanceOpenedCardId.value = cardId;

    autoAdvanceSourceColumnId.value = columnId;
  }

  cardModalOpen.value = true;
}

/*
 * Called whenever the card editor closes.
 *
 * If the card was opened from To Do and it is still in
 * To Do, move it to the top of In Progress.
 *
 * If Save already moved it somewhere else this function
 * simply finds nothing in the original To Do list and
 * exits without doing anything else.
 */
function moveOpenedTodoCardToInProgress() {
  const cardId = autoAdvanceOpenedCardId.value;

  const sourceColumnId = autoAdvanceSourceColumnId.value;

  if (!cardId || !sourceColumnId) {
    return;
  }

  const sourceColumn = getColumn(sourceColumnId);

  const inProgressColumn = getWorkflowColumn("inProgress");

  if (!sourceColumn || !inProgressColumn || sourceColumn.id === inProgressColumn.id) {
    return;
  }

  const sourceIndex = sourceColumn.cards.findIndex((card) => card.id === cardId);

  if (sourceIndex === -1) {
    return;
  }

  const [card] = sourceColumn.cards.splice(sourceIndex, 1);

  card.completed = false;

  card.updatedAt = new Date().toISOString();

  /*
   * Put newly-started work at the top.
   */
  inProgressColumn.cards.unshift(card);

  emit("card-moved", {
    cardId: card.id,

    fromColumnId: sourceColumn.id,

    toColumnId: inProgressColumn.id,

    targetIndex: 0,
  });

  emit("card-updated", clone(card));

  emitBoardChange();
}

function closeCardModal() {
  moveOpenedTodoCardToInProgress();

  cardModalOpen.value = false;

  resetCardEditor();
}

/* =========================================================
   SAVE CARD
========================================================= */

function saveCard() {
  const title = cardEditor.title.trim();

  if (!title) {
    return;
  }

  let targetColumn = getColumn(cardEditorColumnId.value);

  /*
   * CREATE:
   *
   * A new card always goes to To Do.
   */
  if (!cardEditor.id) {
    targetColumn = getWorkflowColumn("todo") || targetColumn;
  }

  /*
   * COMPLETE:
   *
   * Checking Mark card as complete always moves the
   * existing card into Completed.
   */
  if (cardEditor.id && cardEditor.completed) {
    targetColumn = getWorkflowColumn("completed") || targetColumn;
  }

  /*
   * If somebody edits a completed card and unticks its
   * complete status while the selected list remains
   * Completed, send it back to In Progress.
   */
  if (
    cardEditor.id &&
    !cardEditor.completed &&
    columnRole(targetColumn) === "completed"
  ) {
    targetColumn = getWorkflowColumn("inProgress") || targetColumn;
  }

  if (!targetColumn) {
    return;
  }

  cardEditorColumnId.value = targetColumn.id;

  const now = new Date().toISOString();

  const card = {
    id: cardEditor.id || createId("card"),

    title,

    description: cardEditor.description.trim(),

    assignee: cardEditor.assignee.trim(),

    dueDate: cardEditor.dueDate,

    completed: cardEditor.completed,

    labels: cardEditor.labels
      .filter((label) => label.name.trim())
      .map((label) => ({
        ...label,

        name: label.name.trim(),
      })),

    checklist: cardEditor.checklist
      .filter((item) => item.text.trim())
      .map((item) => ({
        ...item,

        text: item.text.trim(),
      })),

    source: cardEditor.source ? clone(cardEditor.source) : null,

    createdAt: cardEditor.createdAt || now,

    updatedAt: now,

    createdBy: cardEditor.createdBy || "",
  };

  /* =========================
     CREATE
  ========================== */

  if (!cardEditor.id) {
    /*
     * New cards always start as incomplete.
     */
    card.completed = false;

    targetColumn.cards.push(card);

    emit("card-created", clone(card));

    emitBoardChange();

    /*
     * A newly-created card was not "opened from To Do",
     * therefore closing here does not auto-start it.
     */
    closeCardModal();

    return;
  }

  /* =========================
     UPDATE
  ========================== */

  const originalColumn = getColumn(originalCardColumnId.value);

  if (!originalColumn) {
    return;
  }

  const index = originalColumn.cards.findIndex(
    (existing) => existing.id === cardEditor.id
  );

  if (index === -1) {
    return;
  }

  /*
   * Make completed flag agree with the destination.
   */
  if (columnRole(targetColumn) === "completed") {
    card.completed = true;
  }

  if (columnRole(targetColumn) === "todo" || columnRole(targetColumn) === "inProgress") {
    card.completed = false;
  }

  if (originalColumn.id === targetColumn.id) {
    originalColumn.cards.splice(index, 1, card);
  } else {
    originalColumn.cards.splice(index, 1);

    targetColumn.cards.push(card);

    emit("card-moved", {
      cardId: card.id,

      fromColumnId: originalColumn.id,

      toColumnId: targetColumn.id,
    });
  }

  emit("card-updated", clone(card));

  emitBoardChange();

  /*
   * If this was originally a To Do card and Save left it
   * in To Do, closeCardModal() now advances it to
   * In Progress.
   *
   * If Save moved it elsewhere already, there is no card
   * remaining in the original To Do column, so the
   * auto-advance becomes a harmless no-op.
   */
  closeCardModal();
}

/* =========================================================
   DELETE CARD
========================================================= */

function deleteCurrentCard() {
  const column = getColumn(originalCardColumnId.value);

  if (!column || !cardEditor.id) {
    return;
  }

  if (!window.confirm(`Delete "${cardEditor.title}"?`)) {
    return;
  }

  column.cards = column.cards.filter((card) => card.id !== cardEditor.id);

  emit("card-deleted", {
    cardId: cardEditor.id,

    columnId: column.id,
  });

  /*
   * Prevent the deleted To Do card from attempting to
   * auto-advance when the modal closes.
   */
  autoAdvanceOpenedCardId.value = null;

  autoAdvanceSourceColumnId.value = null;

  emitBoardChange();

  closeCardModal();
}

/* =========================================================
   LABELS
========================================================= */

const labelColours = [
  "#2e7d32",
  "#f9a825",
  "#ef6c00",
  "#c62828",
  "#7b1fa2",
  "#1565c0",
  "#00796b",
  "#455a64",
];

let labelColourIndex = 0;

function addEditorLabel() {
  const color = labelColours[labelColourIndex % labelColours.length];

  labelColourIndex += 1;

  cardEditor.labels.push({
    id: createId("label"),
    name: "",
    color,
  });
}

function removeEditorLabel(labelId) {
  cardEditor.labels = cardEditor.labels.filter((label) => label.id !== labelId);
}

function labelStyle(label) {
  return {
    backgroundColor: label.color || "#546e7a",

    borderColor: label.color || "#546e7a",

    color: "#ffffff",
  };
}

/* =========================================================
   CHECKLIST
========================================================= */

function addChecklistItem() {
  cardEditor.checklist.push({
    id: createId("check"),
    text: "",
    completed: false,
  });
}

function removeChecklistItem(itemId) {
  cardEditor.checklist = cardEditor.checklist.filter((item) => item.id !== itemId);
}

function checklistTotal(card) {
  return Array.isArray(card.checklist) ? card.checklist.length : 0;
}

function checklistComplete(card) {
  return (card.checklist || []).filter((item) => item.completed).length;
}

const editorChecklistComplete = computed(
  () => cardEditor.checklist.filter((item) => item.completed).length
);

const editorChecklistPercent = computed(() => {
  if (!cardEditor.checklist.length) {
    return 0;
  }

  return Math.round((editorChecklistComplete.value / cardEditor.checklist.length) * 100);
});

/* =========================================================
   DRAG DROP
========================================================= */

const dragState = reactive({
  columnId: null,

  cardId: null,

  overColumnId: null,

  overIndex: null,
});

function onDragStart(event, columnId, cardId) {
  dragState.columnId = columnId;

  dragState.cardId = cardId;

  dragState.overColumnId = columnId;

  const column = getColumn(columnId);

  dragState.overIndex = column?.cards.findIndex((card) => card.id === cardId) ?? 0;

  if (event.dataTransfer) {
    event.dataTransfer.effectAllowed = "move";

    event.dataTransfer.setData("text/plain", cardId);
  }
}

function onColumnDragOver(columnId) {
  if (!dragState.cardId) {
    return;
  }

  dragState.overColumnId = columnId;
}

function setDropPosition(columnId, index) {
  dragState.overColumnId = columnId;

  dragState.overIndex = index;
}

function onDropAtEnd(columnId) {
  const column = getColumn(columnId);

  if (!column) {
    return;
  }

  onDrop(columnId, column.cards.length);
}

function onDrop(targetColumnId, targetIndex) {
  if (!dragState.cardId) {
    return;
  }

  const sourceColumn = getColumn(dragState.columnId);

  const targetColumn = getColumn(targetColumnId);

  if (!sourceColumn || !targetColumn) {
    resetDragState();

    return;
  }

  const sourceIndex = sourceColumn.cards.findIndex(
    (card) => card.id === dragState.cardId
  );

  if (sourceIndex < 0) {
    resetDragState();

    return;
  }

  const [card] = sourceColumn.cards.splice(sourceIndex, 1);

  let insertIndex = targetIndex;

  if (sourceColumn.id === targetColumn.id && sourceIndex < insertIndex) {
    insertIndex -= 1;
  }

  insertIndex = Math.max(0, Math.min(insertIndex, targetColumn.cards.length));

  targetColumn.cards.splice(insertIndex, 0, card);

  /*
   * Dragging into Completed makes the card complete.
   *
   * Dragging a Completed card elsewhere re-opens it.
   */
  if (columnRole(targetColumn) === "completed") {
    card.completed = true;
  } else if (columnRole(sourceColumn) === "completed") {
    card.completed = false;
  }

  /*
   * To Do / In Progress cards should never carry a
   * completed=true flag.
   */
  if (columnRole(targetColumn) === "todo" || columnRole(targetColumn) === "inProgress") {
    card.completed = false;
  }

  card.updatedAt = new Date().toISOString();

  emit("card-moved", {
    cardId: card.id,

    fromColumnId: sourceColumn.id,

    toColumnId: targetColumn.id,

    targetIndex: insertIndex,
  });

  emit("card-updated", clone(card));

  emitBoardChange();

  resetDragState();
}

function onDragEnd() {
  resetDragState();
}

function resetDragState() {
  dragState.columnId = null;

  dragState.cardId = null;

  dragState.overColumnId = null;

  dragState.overIndex = null;
}

/* =========================================================
   WORKFLOW HELPERS
========================================================= */

function normaliseColumnTitle(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, " ");
}

function columnRole(column) {
  const title = normaliseColumnTitle(column?.title);

  if (title === "to do" || title === "todo") {
    return "todo";
  }

  if (title === "in progress" || title === "inprogress") {
    return "inProgress";
  }

  if (title === "done" || title === "complete" || title === "completed") {
    return "completed";
  }

  return null;
}

function getWorkflowColumn(role) {
  return columns.value.find((column) => columnRole(column) === role);
}

/* =========================================================
   GENERAL
========================================================= */

function getColumn(columnId) {
  return columns.value.find((column) => column.id === columnId);
}

function initials(name) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (!parts.length) {
    return "";
  }

  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }

  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function formatDate(value) {
  const parts = String(value || "").split("-");

  if (parts.length !== 3) {
    return value;
  }

  return `${parts[2]}/` + `${parts[1]}/` + `${parts[0].slice(-2)}`;
}

function todayIso() {
  const now = new Date();

  return [
    now.getFullYear(),

    String(now.getMonth() + 1).padStart(2, "0"),

    String(now.getDate()).padStart(2, "0"),
  ].join("-");
}

function isOverdue(card) {
  return Boolean(card.dueDate && !card.completed && card.dueDate < todayIso());
}

function dueDateBadgeClass(card) {
  if (card.completed) {
    return "core-badge--success";
  }

  if (isOverdue(card)) {
    return "core-badge--danger";
  }

  return "core-badge--neutral";
}

/* =========================================================
   DOCUMENT EVENTS
========================================================= */

function handleDocumentClick() {
  closeMenus();
}

function handleKeydown(event) {
  if (event.key !== "Escape") {
    return;
  }

  if (cardModalOpen.value) {
    closeCardModal();

    return;
  }

  if (columnModalOpen.value) {
    closeColumnModal();

    return;
  }

  closeMenus();
}

/* =========================================================
   LIFECYCLE
========================================================= */

onMounted(() => {
  document.addEventListener("click", handleDocumentClick);

  document.addEventListener("keydown", handleKeydown);
});

onBeforeUnmount(() => {
  document.removeEventListener("click", handleDocumentClick);

  document.removeEventListener("keydown", handleKeydown);
});
</script>

<style scoped>
/*
 * Generic styling comes from coredeck-ui.css.
 *
 * This section stays limited to Kanban-specific
 * layout/geometry and the new workflow progress control.
 */

.kanban-board {
  min-width: 0;
}

.kanban-page-header {
  padding: var(--core-space-7) var(--core-space-7) 0;
}

.kanban-board-title-button {
  padding: 0 !important;

  text-align: left;
}

.kanban-board-title-button:hover {
  background: transparent !important;
}

.kanban-board-title-editor {
  max-width: 500px;
}

.kanban-search {
  flex: 1 1 420px;

  max-width: 620px;
}

/* =========================================================
   WORKFLOW PROGRESS
========================================================= */

.kanban-workflow-progress {
  margin: var(--core-space-5) var(--core-space-7) 0;

  padding: var(--core-space-4) var(--core-space-5);

  border: var(--coredeck-border-light);

  border-radius: var(--coredeck-radius-panel);

  background: var(--coredeck-surface);
}

.kanban-workflow-progress-header {
  display: flex;

  align-items: center;

  justify-content: space-between;

  gap: var(--core-space-4);
}

.kanban-workflow-track {
  display: flex;

  width: 100%;

  height: 14px;

  margin-top: var(--core-space-4);

  overflow: hidden;

  border-radius: 999px;

  background: #eceff1;
}

.kanban-workflow-segment {
  height: 100%;

  transition: width 0.2s ease;
}

/*
 * Green = completed work.
 */
.kanban-workflow-segment--completed {
  background: #2e7d32;
}

/*
 * Red = work still in progress.
 */
.kanban-workflow-segment--in-progress {
  background: #c62828;
}

.kanban-workflow-legend {
  display: flex;

  flex-wrap: wrap;

  align-items: center;

  gap: var(--core-space-4);

  margin-top: var(--core-space-3);

  color: var(--coredeck-text-muted);

  font-size: 0.82rem;
}

.kanban-workflow-legend > span {
  display: inline-flex;

  align-items: center;

  gap: var(--core-space-2);
}

.kanban-workflow-dot {
  width: 9px;

  height: 9px;

  border-radius: 50%;
}

.kanban-workflow-dot--completed {
  background: #2e7d32;
}

.kanban-workflow-dot--in-progress {
  background: #c62828;
}

.kanban-workflow-health {
  flex: 0 0 auto;
}

/*
 * Majority Completed
 */
.kanban-workflow-health--green {
  border-color: #a5d6a7 !important;

  background: #e8f5e9 !important;

  color: #1b5e20 !important;
}

/*
 * Exact 50 / 50
 */
.kanban-workflow-health--amber {
  border-color: #ffe082 !important;

  background: #fff8e1 !important;

  color: #8d6e00 !important;
}

/*
 * Majority In Progress
 */
.kanban-workflow-health--red {
  border-color: #ef9a9a !important;

  background: #ffebee !important;

  color: #b71c1c !important;
}

.kanban-workflow-health--neutral {
  border-color: var(--coredeck-neutral-border) !important;

  background: var(--coredeck-neutral-bg) !important;

  color: var(--coredeck-neutral) !important;
}

/* =========================================================
   BOARD
========================================================= */

.kanban-board-scroll {
  width: 100%;

  max-width: 100%;

  padding: var(--core-space-7);

  overflow-x: auto;

  overflow-y: hidden;

  -webkit-overflow-scrolling: touch;
}

.kanban-columns {
  display: flex;

  align-items: flex-start;

  gap: var(--core-space-5);

  min-width: max-content;
}

/*
 * Increased from 300px to 360px.
 *
 * This gives each card more horizontal room without
 * fundamentally changing the Kanban layout.
 */
.kanban-column {
  flex: 0 0 360px;

  width: 360px;

  min-width: 360px;

  margin: 0;

  padding: var(--core-space-4);
}

.kanban-column--drag-target {
  border-color: var(--coredeck-primary);
}

.kanban-column-header {
  position: relative;

  display: flex;

  align-items: center;

  justify-content: space-between;

  gap: var(--core-space-3);

  min-height: 44px;

  margin-bottom: var(--core-space-3);
}

.kanban-column-heading {
  display: flex;

  align-items: center;

  gap: var(--core-space-3);

  flex: 1 1 auto;

  min-width: 0;
}

.kanban-column-title-button {
  flex: 1 1 auto;

  min-width: 0;

  justify-content: flex-start !important;

  text-align: left;
}

.kanban-column-title-input {
  min-width: 0;
}

.kanban-column-menu-wrap {
  position: relative;
}

.kanban-column-menu {
  position: absolute;

  z-index: 50;

  top: 42px;

  right: 0;

  width: 220px;

  padding: var(--core-space-3);
}

.kanban-column-menu .btn-flat {
  width: 100%;

  justify-content: flex-start !important;
}

/* =========================================================
   CARDS
========================================================= */

.kanban-card-list {
  display: flex;

  flex-direction: column;

  min-height: 42px;
}

.kanban-card {
  margin: 0 0 var(--core-space-3);

  cursor: pointer;

  /*
   * Slightly taller card footprint so the cards feel
   * less compressed as well as being wider.
   */
  min-height: 118px;
}

.kanban-card[draggable="true"] {
  cursor: grab;
}

.kanban-card[draggable="true"]:active {
  cursor: grabbing;
}

.kanban-card--dragging {
  opacity: 0.4;
}

.kanban-card-title {
  color: var(--coredeck-text);

  font-size: 0.95rem;

  font-weight: 700;

  line-height: 1.35;

  overflow-wrap: anywhere;
}

.kanban-card-description {
  display: -webkit-box;

  overflow: hidden;

  margin-top: var(--core-space-3);

  -webkit-box-orient: vertical;

  /*
   * Increased from two lines to three so the wider cards
   * also show more useful information at a glance.
   */
  -webkit-line-clamp: 3;
}

.kanban-labels {
  margin-bottom: var(--core-space-3);
}

.kanban-label {
  max-width: 160px;

  overflow: hidden;

  text-overflow: ellipsis;

  white-space: nowrap;
}

.kanban-source {
  margin-bottom: var(--core-space-3);
}

.kanban-card-meta {
  margin-top: var(--core-space-4);
}

.kanban-meta-icon {
  font-size: 14px;

  line-height: 1;
}

.kanban-assignee {
  display: inline-flex;

  align-items: center;

  justify-content: center;

  width: 30px;

  height: 30px;

  margin-left: auto;

  border-radius: 50%;

  background: var(--coredeck-neutral-bg);

  color: var(--coredeck-neutral);

  font-size: 0.7rem;

  font-weight: 800;
}

.kanban-drop-slot {
  height: 4px;

  border-radius: var(--coredeck-radius-control);

  transition: height 0.1s ease, margin 0.1s ease, background-color 0.1s ease;
}

.kanban-drop-slot--active {
  height: 42px;

  margin: var(--core-space-1) 0 var(--core-space-3);

  border: 1px dashed var(--coredeck-neutral-border);

  background: var(--coredeck-surface);
}

.kanban-empty {
  min-height: 72px;
}

.kanban-add-card-button {
  width: 100%;

  justify-content: flex-start !important;
}

.kanban-add-column {
  flex: 0 0 360px;

  width: 360px;

  min-width: 360px;
}

/* =========================================================
   CARD FORM
========================================================= */

.kanban-form-section {
  margin-top: var(--core-space-7);
}

.kanban-complete-hint {
  margin: var(--core-space-2) 0 0 34px;
}

.kanban-label-edit-row {
  display: grid;

  grid-template-columns:
    44px
    minmax(0, 1fr)
    38px;

  align-items: center;

  gap: var(--core-space-3);
}

.kanban-colour-input {
  width: 44px;

  height: 42px;

  padding: 3px;

  border: var(--coredeck-border-medium);

  border-radius: var(--coredeck-radius-control);
}

.kanban-checklist-heading {
  display: flex;

  align-items: center;

  justify-content: space-between;
}

.kanban-checklist-row {
  display: grid;

  grid-template-columns:
    28px
    minmax(0, 1fr)
    38px;

  align-items: center;

  gap: var(--core-space-3);
}

/* =========================================================
   MOBILE
========================================================= */

@media only screen and (max-width: 600px) {
  .kanban-page-header {
    padding-left: var(--coredeck-mobile-gutter);

    padding-right: var(--coredeck-mobile-gutter);
  }

  .kanban-workflow-progress {
    margin-left: var(--coredeck-mobile-gutter);

    margin-right: var(--coredeck-mobile-gutter);
  }

  .kanban-workflow-progress-header {
    align-items: flex-start;

    flex-direction: column;
  }

  .kanban-board-scroll {
    padding: var(--coredeck-mobile-gutter);
  }

  /*
   * Keep mobile usable rather than forcing the wider
   * desktop card size onto a phone.
   */
  .kanban-column,
  .kanban-add-column {
    flex-basis: 275px;

    width: 275px;

    min-width: 275px;
  }

  .kanban-card {
    min-height: 110px;
  }
}
</style>
